import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import { createPortal } from 'react-dom';
import _ from 'lodash';
// eslint-disable-next-line import/no-extraneous-dependencies
import SearchkitClient from '@searchkit/instantsearch-client';
import { JwtRefresher } from '@white-nl/instantsearch-components';
// eslint-disable-next-line import/no-extraneous-dependencies
import {
  Configure,
  InstantSearch,
  useHits,
  useInstantSearch,
  useSearchBox,
} from 'react-instantsearch';
import { Slideout } from '../../components/ui/view/Slideout';
import { FairInteractiveMap as FairInteractiveMapComponent } from '../../components/public/FairInteractiveMap';
import { FairInteractiveMapFilterBar } from '../../components/public/FairInteractiveMapFilterBar';
import { FairInteractiveMapFloorSelector } from '../../components/public/FairInteractiveMapFloorSelector';
import { FairInteractiveMapDetails } from '../../components/public/FairInteractiveMapDetails';
import { Search } from '../../components/ui/icon/Search';
import { Close } from '../../components/ui/icon/Close';

const createSearchClient = (url, token) => {
  const config = {
    url,
    headers: { authorization: `Bearer ${token}` },
  };

  return Object.assign(SearchkitClient(config), {
    transporter: {
      headers: {
        'x-algolia-application-id': 'NULL',
        'x-algolia-api-key': 'NULL',
      },
    },
  });
};

const normalizeSearchText = (value) => `${value || ''}`
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .replace(/[^a-z0-9\s]/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const levenshteinDistance = (left, right) => {
  if (left === right) {
    return 0;
  }
  if (!left.length) {
    return right.length;
  }
  if (!right.length) {
    return left.length;
  }

  let previousRow = Array.from({ length: right.length + 1 }, (unused, index) => index);
  let currentRow = new Array(right.length + 1);

  for (let leftIndex = 0; leftIndex < left.length; leftIndex += 1) {
    currentRow[0] = leftIndex + 1;

    for (let rightIndex = 0; rightIndex < right.length; rightIndex += 1) {
      const substitutionCost = left[leftIndex] === right[rightIndex] ? 0 : 1;

      currentRow[rightIndex + 1] = Math.min(
        currentRow[rightIndex] + 1,
        previousRow[rightIndex + 1] + 1,
        previousRow[rightIndex] + substitutionCost,
      );
    }

    const tempRow = previousRow;
    previousRow = currentRow;
    currentRow = tempRow;
  }

  return previousRow[right.length];
};

const maxTypoDistance = (queryPartLength) => {
  if (queryPartLength <= 3) {
    return 1;
  }
  if (queryPartLength <= 7) {
    return 2;
  }
  return 3;
};

const getFuzzyLabelScore = (normalizedQuery, normalizedLabel) => {
  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  const labelTokens = normalizedLabel.split(' ').filter(Boolean);

  if (queryTokens.length === 0 || labelTokens.length === 0) {
    return null;
  }

  let totalDistance = 0;
  for (let index = 0; index < queryTokens.length; index += 1) {
    const queryToken = queryTokens[index];
    const maxDistance = maxTypoDistance(queryToken.length);

    const shortestDistance = labelTokens.reduce((bestDistance, labelToken) => (
      Math.min(bestDistance, levenshteinDistance(queryToken, labelToken))
    ), Number.POSITIVE_INFINITY);

    if (shortestDistance > maxDistance) {
      return null;
    }

    totalDistance += shortestDistance;
  }

  return totalDistance;
};

function FairSectionsFilter({ sections, selectedSections, onSectionToggle }) {
  return (
    <ul>
      {sections.map((section) => {
        const inputId = `fairSections-${section}`;
        const checked = selectedSections.includes(section);

        return (
          <li key={section}>
            <input
              id={inputId}
              type="checkbox"
              checked={checked}
              onChange={() => onSectionToggle(section)}
            />
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
            <label htmlFor={inputId}>
              <span>
                <span>{section}</span>
              </span>
            </label>
          </li>
        );
      })}
    </ul>
  );
}

FairSectionsFilter.propTypes = {
  sections: PropTypes.arrayOf(PropTypes.string).isRequired,
  selectedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  onSectionToggle: PropTypes.func.isRequired,
};

function ExhibitorSearchBox({
  query,
  options,
  onInputChange,
  onOptionSelect,
  onQuerySubmit,
  onQueryClear,
}) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const hasSuggestions = query.trim().length > 0 && options.length > 0;

  return (
    <div className="fair-interactive-map-search-box">
      <input
        type="search"
        value={query}
        onFocus={() => setIsDropdownOpen(true)}
        onChange={(event) => {
          onInputChange(event.target.value);
          setIsDropdownOpen(true);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            onQuerySubmit(query);
            setIsDropdownOpen(false);
          }
        }}
        onBlur={() => {
          window.setTimeout(() => setIsDropdownOpen(false), 100);
        }}
        placeholder="Search for exhibitors"
      />
      {isDropdownOpen && hasSuggestions && (
        <ul className="fair-interactive-map-search-suggestions">
          {options.map((option) => (
            <li key={`${option.id}-${option.standNumber}`}>
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onOptionSelect(option);
                  setIsDropdownOpen(false);
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
      <div className="fair-interactive-map-search-button-group">
        {query && (
          <button
            type="button"
            className="icon-wrapper"
            aria-label="Clear search"
            onClick={onQueryClear}
          >
            <Close />
          </button>
        )}
        <div className="icon-wrapper">
          <Search />
        </div>
      </div>
    </div>
  );
}

ExhibitorSearchBox.propTypes = {
  query: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    label: PropTypes.string.isRequired,
    standNumber: PropTypes.string.isRequired,
  })),
  onInputChange: PropTypes.func.isRequired,
  onOptionSelect: PropTypes.func.isRequired,
  onQuerySubmit: PropTypes.func.isRequired,
  onQueryClear: PropTypes.func.isRequired,
};

ExhibitorSearchBox.defaultProps = {
  options: [],
};

function ExhibitorsSearchState(
  {
    fairId,
    floors,
    pois,
    mapBgColor,
    hideHome,
    children,
    selectedStand,
    selectedFloor,
    selectedSections,
    setSelectedStand,
    setSelectedFloor,
    setSelectedSections,
    actionsRef,
    onSelectedExhibitorChange,
  },
) {
  const { results } = useInstantSearch();
  const { items } = useHits();
  const { query, refine } = useSearchBox();
  const [searchInput, setSearchInput] = useState('');
  const [standInFocusFromQuery, setStandInFocusFromQuery] = useState(null);
  const [allExhibitorStands, setAllExhibitorStands] = useState(null);
  const [allFairSections, setAllFairSections] = useState([]);

  const exhibitorsByStand = useMemo(() => {
    const nextStands = {};

    items.forEach((hit) => {
      const participation = hit?.participationsPerFair?.[fairId] || null;
      if (!participation) {
        return;
      }

      const standNumber = participation.standNumber ? participation.standNumber.trim() : null;
      if (!standNumber) {
        return;
      }

      nextStands[standNumber] = {
        id: hit.id,
        title: hit.title,
        url: window.Craft.getSiteUrl(hit.uri),
        fairSections: Array.isArray(participation.fairSections)
          ? participation.fairSections
          : [participation.fairSections],
        standNumber,
        image: _.get(hit, 'locations[0].image.transforms.catalog.url')
          || _.get(hit, 'image.url')
          || _.get(hit, 'logo.url')
          || _.get(hit, 'images.0.url')
          || null,
      };
    });
    return nextStands;
  }, [items, fairId]);

  useEffect(() => {
    if (allExhibitorStands !== null) {
      return;
    }

    const standKeys = Object.keys(exhibitorsByStand);
    if (standKeys.length === 0) {
      return;
    }

    setAllExhibitorStands(exhibitorsByStand);

    const sectionsSet = new Set();
    Object.values(exhibitorsByStand).forEach((exhibitor) => {
      (exhibitor.fairSections || []).forEach((section) => section && sectionsSet.add(`${section}`));
    });
    setAllFairSections(Array.from(sectionsSet).sort());
  }, [exhibitorsByStand, allExhibitorStands]);

  const currentFairSections = useMemo(() => {
    const sectionsSet = new Set();
    Object.values(exhibitorsByStand).forEach((exhibitor) => {
      (exhibitor.fairSections || []).forEach((section) => sectionsSet.add(`${section}`));
    });
    return Array.from(sectionsSet).sort();
  }, [exhibitorsByStand]);

  const fairSections = allFairSections.length > 0 ? allFairSections : currentFairSections;

  const selectedExhibitor = selectedStand
    ? (
      allExhibitorStands?.[`${selectedStand}`]
      || exhibitorsByStand[`${selectedStand}`]
      || null
    )
    : null;

  useEffect(() => {
    onSelectedExhibitorChange(selectedExhibitor);
  }, [selectedExhibitor, onSelectedExhibitorChange]);

  const updateStandFromQuery = useCallback((standKey) => {
    const exhibitorStands = allExhibitorStands || exhibitorsByStand;
    if (!standKey || !exhibitorStands?.[standKey]) {
      setStandInFocusFromQuery(null);
      return;
    }
    setStandInFocusFromQuery(standKey);
  }, [allExhibitorStands, exhibitorsByStand]);

  const exhibitorOptions = useMemo(
    () => Object.values(allExhibitorStands || exhibitorsByStand)
      .map((exhibitor) => ({
        id: exhibitor.id,
        label: exhibitor.title || '',
        standNumber: exhibitor.standNumber,
      })),
    [allExhibitorStands, exhibitorsByStand],
  );

  const autocompleteOptions = useMemo(() => {
    const normalizedInput = normalizeSearchText(searchInput);
    if (!normalizedInput) {
      return [];
    }

    return exhibitorOptions
      .map((option) => {
        const normalizedLabel = normalizeSearchText(option.label);
        const normalizedStandNumber = normalizeSearchText(option.standNumber);

        if (normalizedStandNumber.includes(normalizedInput)) {
          return { option, score: 0 };
        }

        if (normalizedLabel.startsWith(normalizedInput)) {
          return { option, score: 1 };
        }

        if (normalizedLabel.includes(normalizedInput)) {
          return { option, score: 2 };
        }

        const fuzzyScore = getFuzzyLabelScore(normalizedInput, normalizedLabel);
        if (fuzzyScore === null) {
          return null;
        }

        return { option, score: 10 + fuzzyScore };
      })
      .filter(Boolean)
      .sort((left, right) => {
        if (left.score !== right.score) {
          return left.score - right.score;
        }
        return left.option.label.localeCompare(right.option.label);
      })
      .slice(0, 10)
      .map(({ option }) => option);
  }, [searchInput, exhibitorOptions]);

  const findExactOption = useCallback((inputValue) => {
    const normalizedInput = normalizeSearchText(inputValue);
    if (!normalizedInput) {
      return null;
    }

    return exhibitorOptions.find(
      (option) => normalizeSearchText(option.label) === normalizedInput
        || normalizeSearchText(option.standNumber) === normalizedInput,
    ) || null;
  }, [exhibitorOptions]);

  const applySearchSelection = useCallback((option, fallbackQuery = '') => {
    const nextQuery = (fallbackQuery === option?.standNumber) ? fallbackQuery : option?.label || fallbackQuery || '';
    refine(nextQuery);
    setSearchInput(nextQuery);
    updateStandFromQuery(option?.standNumber || null);
    setSelectedSections([]);
    setSelectedFloor(null);
    setSelectedStand(null);
  }, [refine, updateStandFromQuery, setSelectedSections, setSelectedFloor, setSelectedStand]);

  useEffect(() => {
    setSearchInput(query || '');
  }, [query]);

  const setValue = useCallback((key, value) => {
    if (key === 'q') {
      const nextQuery = value || '';
      setSearchInput(nextQuery);
      refine(nextQuery);
      const matchedOption = findExactOption(nextQuery);
      updateStandFromQuery(matchedOption?.standNumber || null);
      if (!nextQuery) {
        setSelectedStand(null);
      }
      return;
    }

    if (key === 'fairSections') {
      setSelectedSections(Array.isArray(value) ? value : []);
    }
  }, [refine, findExactOption, updateStandFromQuery, setSelectedSections, setSelectedStand]);

  const clearValues = useCallback(() => {
    setSelectedSections([]);
    setSearchInput('');
    refine('');
    setStandInFocusFromQuery(null);
    setSelectedStand(null);
  }, [refine, setSelectedSections, setSelectedStand]);

  useEffect(() => {
    const actions = actionsRef;
    actions.current = { setValue, clearValues };
    return () => {
      actions.current = { setValue: null, clearValues: null };
    };
  }, [actionsRef, setValue, clearValues]);

  const handleSectionToggle = useCallback((section) => {
    setSelectedSections((current) => (
      current.includes(section)
        ? current.filter((item) => item !== section)
        : [...current, section]
    ));
    setSelectedFloor(null);
    refine('');
    setStandInFocusFromQuery(null);
    setSelectedStand(null);
  }, [refine, setSelectedFloor, setSelectedSections, setSelectedStand]);

  const handleQueryInputChange = useCallback((newQuery) => {
    setSearchInput(newQuery);
    if (query && newQuery !== query) {
      refine(newQuery);
    }
    updateStandFromQuery(null);
    setSelectedStand(null);
  }, [query, refine, updateStandFromQuery, setSelectedStand]);

  const handleOptionSelect = useCallback((option) => {
    applySearchSelection(option);
  }, [applySearchSelection]);

  const handleQuerySubmit = useCallback((inputValue) => {
    const exactMatch = findExactOption(inputValue);
    const option = exactMatch || autocompleteOptions[0] || null;
    applySearchSelection(option, inputValue);
  }, [findExactOption, autocompleteOptions, applySearchSelection]);

  const handleQueryClear = useCallback(() => {
    setSearchInput('');
    refine('');
    setStandInFocusFromQuery(null);
    setSelectedStand(null);
  }, [refine, setSelectedStand]);

  const handleFloorChange = useCallback((floor) => {
    setSelectedFloor(floor);
    setSelectedSections([]);
    refine('');
    setStandInFocusFromQuery(null);
    setSelectedStand(null);
  }, [refine, setSelectedFloor, setSelectedSections, setSelectedStand]);

  const handleStandClick = useCallback((standNumber) => {
    const standKey = `${standNumber}`;
    setSelectedStand(standKey);
    const exhibitor = allExhibitorStands?.[standKey] || exhibitorsByStand[standKey];
    if (query && exhibitor) {
      refine(exhibitor.title || '');
    }
  }, [allExhibitorStands, exhibitorsByStand, query, refine, setSelectedStand]);

  const handleOutsideClick = useCallback(() => {
    if (selectedStand) {
      setSelectedStand(null);
      refine('');
      setStandInFocusFromQuery(null);
    }
  }, [selectedStand, refine, setSelectedStand]);

  const filteredStands = useMemo(() => {
    const stands = Object.values(exhibitorsByStand);
    if (selectedSections.length === 0 && !standInFocusFromQuery) {
      return stands.map((exhibitor) => exhibitor.standNumber);
    }

    return stands
      .filter((exhibitor) => {
        const sections = (exhibitor.fairSections || []).map((section) => `${section}`);
        return selectedSections.some((section) => sections.includes(section));
      })
      .map((exhibitor) => exhibitor.standNumber);
  }, [exhibitorsByStand, selectedSections, standInFocusFromQuery]);

  const allStandsInFocus = useMemo(() => {
    const selectedFloorFilter = ((floors || [])[selectedFloor] || []).map((stand) => `${stand}`);
    const hasAnyFilters = selectedSections.length > 0 || !!query;

    const allStands = [
      ...(hasAnyFilters || standInFocusFromQuery ? [] : (pois || [])),
      ...filteredStands,
      standInFocusFromQuery,
      selectedExhibitor ? selectedExhibitor.standNumber : null,
    ]
      .filter(Boolean)
      .map((stand) => `${stand}`);

    const uniqueStands = Array.from(new Set(allStands));

    if (selectedFloorFilter.length === 0) {
      return uniqueStands;
    }

    return uniqueStands.filter((stand) => selectedFloorFilter.includes(stand));
  }, [
    floors,
    selectedFloor,
    selectedSections,
    query,
    pois,
    filteredStands,
    standInFocusFromQuery,
    selectedExhibitor,
  ]);

  // eslint-disable-next-line no-underscore-dangle
  const isLoaded = allExhibitorStands !== null || !results?.__isArtificial;

  return (
    <>
      <FairInteractiveMapFilterBar
        numberOfResults={allStandsInFocus.length}
        selectedCategories={selectedSections}
        setValue={setValue}
        clearValues={clearValues}
        renderFilterList={fairSections.length > 0 ? (
          <FairSectionsFilter
            sections={fairSections}
            selectedSections={selectedSections}
            onSectionToggle={handleSectionToggle}
          />
        ) : null}
        renderFloorSelector={floors ? (
          <FairInteractiveMapFloorSelector
            data={Object.keys(floors).filter(Boolean)}
            value={selectedFloor}
            onChange={handleFloorChange}
          />
        ) : null}
        renderSearchBox={(
          <ExhibitorSearchBox
            query={searchInput}
            options={autocompleteOptions}
            onInputChange={handleQueryInputChange}
            onOptionSelect={handleOptionSelect}
            onQuerySubmit={handleQuerySubmit}
            onQueryClear={handleQueryClear}
          />
        )}
      />
      {isLoaded && (
        <FairInteractiveMapComponent
          stands={Object.keys(allExhibitorStands || exhibitorsByStand)}
          pois={pois}
          standsInFocus={allStandsInFocus}
          bgColor={mapBgColor || '#292929'}
          disableBgColor="#505050"
          notInFocusOpacity={0.4}
          withoutHome={hideHome}
          onStandClick={handleStandClick}
          onOutsideClick={handleOutsideClick}
        >
          {children}
        </FairInteractiveMapComponent>
      )}
    </>
  );
}

ExhibitorsSearchState.propTypes = {
  fairId: PropTypes.string.isRequired,
  floors: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ),
  pois: PropTypes.arrayOf(PropTypes.string),
  mapBgColor: PropTypes.string,
  hideHome: PropTypes.bool,
  children: PropTypes.shape({ id: PropTypes.string }),
  selectedStand: PropTypes.string,
  selectedFloor: PropTypes.string,
  selectedSections: PropTypes.arrayOf(PropTypes.string).isRequired,
  setSelectedStand: PropTypes.func.isRequired,
  setSelectedFloor: PropTypes.func.isRequired,
  setSelectedSections: PropTypes.func.isRequired,
  actionsRef: PropTypes.shape({
    current: PropTypes.shape({
      setValue: PropTypes.func,
      clearValues: PropTypes.func,
    }),
  }).isRequired,
  onSelectedExhibitorChange: PropTypes.func.isRequired,
};

ExhibitorsSearchState.defaultProps = {
  floors: null,
  pois: [],
  mapBgColor: null,
  hideHome: false,
  children: null,
  selectedStand: null,
  selectedFloor: null,
};

function ExhibitorsDetailsPortal(
  {
    selectedExhibitor,
    onClose,
  },
) {
  const { items } = useHits();

  const exhibitorArtworks = useMemo(() => {
    if (!selectedExhibitor) {
      return [];
    }

    const exhibitorId = selectedExhibitor.id;
    const exhibitorStand = selectedExhibitor.standNumber;

    return items
      .filter((hit) => {
        const accountId = hit?.account?.id || hit?.accountId || hit?.exhibitorId;
        if (exhibitorId && accountId === exhibitorId) {
          return true;
        }

        const standNumber = hit?.standNumber || hit?.fairParticipation?.standNumber;
        return !!(exhibitorStand && standNumber && exhibitorStand === standNumber);
      })
      .map((hit) => ({
        id: hit.id,
        url: window.Craft.getSiteUrl(hit.uri),
        title: hit.title || '',
        year: hit.year || null,
        artist: [hit.attribution, hit.primaryArtistName].filter(Boolean).join(' ').trim(),
        image: _.get(hit, 'image.transforms.thumbnail.url')
          || _.get(hit, 'image.transforms.catalog.url')
          || _.get(hit, 'image.url'),
      }));
  }, [items, selectedExhibitor]);

  return createPortal(
    <Slideout
      isOpen={!!selectedExhibitor}
      onClose={onClose}
      headingLabel="Exhibitor"
      // eslint-disable-next-line react/jsx-no-useless-fragment
      headingIcon={(<></>)}
      hideButtons
      backdropBehavior="click-through"
    >
      <FairInteractiveMapDetails
        exhibitor={selectedExhibitor}
        artworks={exhibitorArtworks}
      />
    </Slideout>,
    document.body,
  );
}

ExhibitorsDetailsPortal.propTypes = {
  selectedExhibitor: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    title: PropTypes.string,
    url: PropTypes.string,
    standNumber: PropTypes.string,
    image: PropTypes.string,
  }),
  onClose: PropTypes.func.isRequired,
};

ExhibitorsDetailsPortal.defaultProps = {
  selectedExhibitor: null,
};

function FairInteractiveMap(
  {
    exhibitorsPublicUrl,
    exhibitorsIndexName,
    artworksPublicUrl,
    artworksIndexName,
    refreshUrl,
    jwt,
    fairTitle,
    fairId,
    showArtworks,
    pois,
    mapBgColor,
    hideHome,
    floors,
    children,
  },
) {
  const actionsRef = useRef({ setValue: null, clearValues: null });
  const [selectedStand, setSelectedStand] = useState(null);
  const [selectedFloor, setSelectedFloor] = useState(null);
  const [selectedSections, setSelectedSections] = useState([]);
  const [selectedExhibitor, setSelectedExhibitor] = useState(null);

  const exhibitorsSearchClient = useMemo(
    () => createSearchClient(exhibitorsPublicUrl, jwt),
    [exhibitorsPublicUrl, jwt],
  );
  const artworksSearchClient = useMemo(
    () => createSearchClient(artworksPublicUrl, jwt),
    [artworksPublicUrl, jwt],
  );

  const [currentExhibitorsSearchClient, setCurrentExhibitorsSearchClient] = useState(
    exhibitorsSearchClient,
  );
  const [currentArtworksSearchClient, setCurrentArtworksSearchClient] = useState(
    artworksSearchClient,
  );

  useEffect(() => {
    setCurrentExhibitorsSearchClient(exhibitorsSearchClient);
  }, [exhibitorsSearchClient]);

  useEffect(() => {
    setCurrentArtworksSearchClient(artworksSearchClient);
  }, [artworksSearchClient]);

  const exhibitorsConfigureProps = {
    hitsPerPage: 999,
    facetFilters: [[`fair:${fairTitle}`]],
  };

  const artworksConfigureProps = {
    hitsPerPage: 999,
    facetFilters: [[`fair:${fairTitle}`]],
  };

  const handleCloseExhibitorDetails = useCallback(() => {
    setSelectedStand(null);
    setSelectedExhibitor(null);
    if (actionsRef.current.setValue) {
      actionsRef.current.setValue('q', null);
    }
  }, []);

  return (
    <>
      <JwtRefresher
        initialToken={jwt}
        cookieName="elasticsearch_token"
        checkIntervalSeconds={5}
        refreshTimeBeforeExpirySeconds={30}
        refreshIframeUrl={refreshUrl}
        onRefreshed={(token) => {
          setCurrentExhibitorsSearchClient(createSearchClient(exhibitorsPublicUrl, token));
        }}
      />
      <JwtRefresher
        initialToken={jwt}
        cookieName="elasticsearch_token"
        checkIntervalSeconds={5}
        refreshTimeBeforeExpirySeconds={30}
        refreshIframeUrl={refreshUrl}
        onRefreshed={(token) => {
          setCurrentArtworksSearchClient(createSearchClient(artworksPublicUrl, token));
        }}
      />
      <InstantSearch
        indexName={exhibitorsIndexName}
        searchClient={currentExhibitorsSearchClient}
        future={{ preserveSharedStateOnUnmount: true }}
      >
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <Configure {...exhibitorsConfigureProps} />
        <ExhibitorsSearchState
          fairId={fairId}
          floors={floors}
          pois={pois}
          mapBgColor={mapBgColor}
          hideHome={hideHome}
          selectedStand={selectedStand}
          selectedFloor={selectedFloor}
          selectedSections={selectedSections}
          setSelectedStand={setSelectedStand}
          setSelectedFloor={setSelectedFloor}
          setSelectedSections={setSelectedSections}
          actionsRef={actionsRef}
          onSelectedExhibitorChange={setSelectedExhibitor}
        >
          {children}
        </ExhibitorsSearchState>
      </InstantSearch>
      {showArtworks
        ? (
          <InstantSearch
            indexName={artworksIndexName}
            searchClient={currentArtworksSearchClient}
            future={{ preserveSharedStateOnUnmount: true }}
          >
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <Configure {...artworksConfigureProps} />
            <ExhibitorsDetailsPortal
              selectedExhibitor={selectedExhibitor}
              onClose={handleCloseExhibitorDetails}
            />
          </InstantSearch>
        )
        : createPortal(
          <Slideout
            isOpen={!!selectedExhibitor}
            onClose={handleCloseExhibitorDetails}
            headingLabel="Exhibitor"
            // eslint-disable-next-line react/jsx-no-useless-fragment
            headingIcon={(<></>)}
            hideButtons
            backdropBehavior="click-through"
          >
            <FairInteractiveMapDetails
              exhibitor={selectedExhibitor}
              artworks={[]}
            />
          </Slideout>,
          document.body,
        )}
    </>
  );
}

export { FairInteractiveMap };

FairInteractiveMap.propTypes = {
  exhibitorsPublicUrl: PropTypes.string.isRequired,
  exhibitorsIndexName: PropTypes.string.isRequired,
  artworksPublicUrl: PropTypes.string.isRequired,
  artworksIndexName: PropTypes.string.isRequired,
  refreshUrl: PropTypes.string.isRequired,
  jwt: PropTypes.string.isRequired,
  fairTitle: PropTypes.string.isRequired,
  fairId: PropTypes.string.isRequired,
  showArtworks: PropTypes.bool,
  pois: PropTypes.arrayOf(PropTypes.string),
  mapBgColor: PropTypes.string,
  hideHome: PropTypes.bool,
  floors: PropTypes.objectOf(
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  ),
  children: PropTypes.shape({ id: PropTypes.string }),
};

FairInteractiveMap.defaultProps = {
  showArtworks: false,
  pois: [],
  mapBgColor: null,
  hideHome: false,
  floors: null,
  children: null,
};
